import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const directors = [
  {
    name: 'Annie Bender',
    instrument: 'Violin',
    conservatory: 'The Juilliard School & Colburn Conservatory of Music',
    role: 'Founder & CEO',
    bio: 'Annie Bender is the Founder & CEO of The Salon Society, and a graduate of The Juilliard School and the Colburn Conservatory of Music. She has performed across the United States in notable venues including Carnegie Hall, The Kennedy Center, Lincoln Center, Walt Disney Concert Hall, The Shrine Auditorium, The Aspen Music Festival and School, Schermerhorn Symphony Hall, and the American Museum of Natural History. She has appeared as soloist with the Nashville Symphony Orchestra, the Hilton Head Symphony Orchestra, the Paducah Symphony, and the Nashville Philharmonic Orchestra. Annie has served as Concertmaster of the Aspen Philharmonic Orchestra and the Young Artists Symphony Orchestra. As a recording artist, she has played on orchestral film scores for Netflix and Warner Brothers, and appeared on albums for artists including Prince, Judith Hill, and Tyler the Creator. She is prizewinner of the Curb Records Competition, the MTNA National Solo Competition, and the ASTA National Competition. Annie lives in Franklin, TN.',
    accolades: [
      'Founder & CEO — The Salon Society',
      'Graduate — The Juilliard School (Irene Diamond Graduate Fellowship)',
      'Teaching Fellowship — Colburn Conservatory of Music',
      'Soloist — Nashville Symphony Orchestra',
      'Concertmaster — Aspen Philharmonic Orchestra',
      'Grammy\'s Lifetime Achievement Awards Ceremony Performer',
      'Film Scores: Netflix & Warner Brothers',
      'Prizewinner — MTNA National Solo Competition & ASTA National Competition',
    ],
    image: 'https://public.readdy.ai/ai/img_res/edited_24d9e6f8d3936df90ea16e430ff0b8a0_06976282.jpg',
    objectPosition: 'center top',
  },
  {
    name: 'Landon Shaw II',
    instrument: 'Tenor',
    conservatory: 'USC Thornton School of Music · LA Opera Buffs · Aspen Music Festival',
    role: 'Co-founder',
    bio: 'Landon Shaw II is an acclaimed tenor whose training spans some of the most prestigious institutions in classical music. A product of the USC Thornton School of Music, he has honed his craft through the Aspen Music Festival and School, performed with LA Opera Buffs, and brought his voice to the innovative stages of Hopscotch Opera. Known for his powerful, expressive delivery and commanding stage presence, Landon brings rare vocal brilliance and dramatic depth to every performance — from grand concert halls to the most intimate private salons.',
    accolades: [
      'Co-founder — The Salon Society',
      'USC Thornton School of Music',
      'LA Opera Buffs',
      'Aspen Music Festival and School',
      'Hopscotch Opera',
      'Featured Tenor Soloist — Hollywood Chamber Orchestra',
      'Opera Gala Benefit Concert — St. Monica Catholic Community Church',
    ],
    image: 'https://public.readdy.ai/ai/img_res/edited_ecaaa2259c0709a4bf57e266d2fedb2f_fbe2db76.jpg',
    objectPosition: 'center top',
  },
];

export default function ArtisticDirectors() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const headerAnim = useScrollAnimation({ threshold: 0.2 });
  const bannerAnim = useScrollAnimation({ threshold: 0.15 });
  const card0Anim = useScrollAnimation({ threshold: 0.1 });
  const card1Anim = useScrollAnimation({ threshold: 0.1 });
  const stripAnim = useScrollAnimation({ threshold: 0.1 });

  const cardAnims = [card0Anim, card1Anim];

  return (
    <section id="artistic-directors" className="py-24 lg:py-32" style={{ backgroundColor: '#faf8f5' }}>
      <div className="max-w-6xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div
          ref={headerAnim.ref as React.RefObject<HTMLDivElement>}
          className="text-center mb-6 transition-all duration-700"
          style={{
            opacity: headerAnim.isVisible ? 1 : 0,
            transform: headerAnim.isVisible ? 'translateY(0)' : 'translateY(32px)',
          }}
        >
          <p className="text-xs uppercase tracking-[0.25em] text-amber-700 font-medium mb-4">
            Leadership
          </p>
          <h2 className="font-serif text-4xl lg:text-5xl text-gray-900 mb-6 leading-tight">
            Meet Our Artistic Directors
          </h2>
          <div className="w-16 h-px bg-amber-600 mx-auto mb-8" />
        </div>

        {/* Elite Conservatory Banner */}
        <div
          ref={bannerAnim.ref as React.RefObject<HTMLDivElement>}
          className="relative rounded-2xl overflow-hidden mb-16 px-8 py-10 text-center transition-all duration-700"
          style={{
            background: 'linear-gradient(135deg, #1a1208 0%, #2e1f0a 50%, #1a1208 100%)',
            opacity: bannerAnim.isVisible ? 1 : 0,
            transform: bannerAnim.isVisible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.98)',
          }}
        >
          <div className="absolute inset-0 opacity-10"
            style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, #d4a843 0%, transparent 60%), radial-gradient(circle at 70% 50%, #d4a843 0%, transparent 60%)' }} />
          <p className="relative font-serif text-xl lg:text-2xl text-amber-200 leading-relaxed max-w-3xl mx-auto italic">
            "Every artist at The Salon Society is trained at the most elite conservatories in the world —
            Juilliard, Colburn, the Cleveland Institute, USC Thornton, and the stages of Aspen and Verbier — and has performed at the highest levels of the profession."
          </p>
          <div className="relative mt-6 flex items-center justify-center gap-3">
            <div className="w-8 h-px bg-amber-600" />
            <span className="text-amber-500 text-xs uppercase tracking-[0.2em]">Our Standard of Excellence</span>
            <div className="w-8 h-px bg-amber-600" />
          </div>
        </div>

        {/* Director Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {directors.map((director, index) => (
            <div
              key={director.name}
              ref={cardAnims[index].ref as React.RefObject<HTMLDivElement>}
              className="group cursor-pointer transition-all duration-700"
              style={{
                opacity: cardAnims[index].isVisible ? 1 : 0,
                transform: cardAnims[index].isVisible
                  ? 'translateY(0)'
                  : `translateY(48px)`,
                transitionDelay: `${index * 150}ms`,
              }}
              onClick={() => setActiveIndex(activeIndex === index ? null : index)}
            >
              <div className="relative rounded-xl overflow-hidden shadow-md transition-all duration-300 group-hover:shadow-xl">
                {/* Photo */}
                <div className="w-full h-80 overflow-hidden">
                  <img
                    src={director.image}
                    alt={director.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    style={{ objectPosition: (director as any).objectPosition || 'top' }}
                  />
                </div>

                {/* Overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-80" />

                {/* Founder Badge */}
                {director.role && (
                  <div className="absolute top-4 left-4">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest bg-amber-600 text-white shadow-lg">
                      <i className="ri-vip-crown-fill text-xs" />
                      {director.role}
                    </span>
                  </div>
                )}

                {/* Name & Instrument */}
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-1">{director.instrument}</p>
                  <h3 className="font-serif text-xl text-white leading-tight">{director.name}</h3>
                  <p className="text-xs text-gray-300 mt-1 leading-snug">{director.conservatory}</p>
                </div>

                {/* Expand indicator */}
                <div className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm transition-transform duration-300 group-hover:bg-amber-600/80">
                  <i className={`ri-${activeIndex === index ? 'subtract' : 'add'}-line text-white text-sm`} />
                </div>
              </div>

              {/* Expanded Bio */}
              {activeIndex === index && (
                <div className="mt-3 rounded-xl bg-white border border-amber-100 shadow-lg p-5 animate-fade-in">
                  {director.role && (
                    <div className="flex items-center gap-2 mb-3">
                      <i className="ri-vip-crown-fill text-amber-600 text-sm" />
                      <span className="text-xs font-semibold uppercase tracking-widest text-amber-700">{director.role}</span>
                    </div>
                  )}
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">{director.bio}</p>
                  <ul className="space-y-1">
                    {director.accolades.map((a) => (
                      <li key={a} className="flex items-start gap-2 text-xs text-gray-600">
                        <span className="mt-0.5 w-3 h-3 flex items-center justify-center flex-shrink-0">
                          <i className="ri-medal-line text-amber-600 text-xs" />
                        </span>
                        {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom Credential Strip */}
        <div
          ref={stripAnim.ref as React.RefObject<HTMLDivElement>}
          className="mt-16 border-t border-amber-200 pt-10 transition-all duration-700"
          style={{
            opacity: stripAnim.isVisible ? 1 : 0,
            transform: stripAnim.isVisible ? 'translateY(0)' : 'translateY(24px)',
          }}
        >
          <p className="text-center text-xs uppercase tracking-[0.2em] text-gray-500 mb-6">
            Our Artists Have Trained &amp; Performed At
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4">
            {[
              'The Juilliard School',
              'The Colburn Conservatory of Music',
              'The Aspen Music Festival and School',
              'USC Thornton School of Music',
              'LA Opera Buffs',
              'Hopscotch Opera',
              'Cleveland Institute of Music',
              'The Cleveland Orchestra',
              'The Philharmonia Orchestra, London',
              'Nashville Chamber Music Society',
              'Mercer University',
              'Carnegie Hall',
              'Carnegie Hall\'s National Youth Orchestra of the USA',
              'NPR\'s From the Top',
              'Vanderbilt University\'s Blair School of Music',
              'The Kennedy Center',
              'Lincoln Center',
              'Walt Disney Concert Hall',
              'The Shrine Auditorium',
              'Schermerhorn Symphony Hall',
              'American Museum of Natural History',
              'Nashville Symphony Orchestra',
              'Hilton Head Symphony Orchestra',
              'Paducah Symphony',
              'Nashville Philharmonic Orchestra',
              'Aspen Philharmonic Orchestra',
              'Young Artists Symphony Orchestra',
              'Hollywood Chamber Orchestra',
              'St. Monica Catholic Community Church',
            ].map((school) => (
              <span key={school} className="font-serif text-sm text-gray-700 italic whitespace-nowrap">
                {school}
              </span>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}